/**
 * Braintree Payment Service
 * Handles Venmo, PayPal, and Credit Card payments via Braintree
 */

import { Platform, Alert, Linking } from 'react-native';
import { BraintreeDropIn } from 'react-native-braintree-payments-drop-in';
import twilioService from './twilioService';
import emailService from './emailService';
import notificationService from './notificationService';

// For web, use Braintree Web SDK
let braintreeWeb: any = null;
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  try {
    // Dynamically import braintree-web for web platform
    braintreeWeb = require('braintree-web');
  } catch (e) {
    console.warn('braintree-web not available. PayPal on web will not work until package is installed.');
  }
}

export const BRAINTREE_CONFIG = {
  // Braintree tokenization key - use environment variable in production
  // Production keys start with a production_ prefix, sandbox keys start with sandbox_
  tokenizationKey: process.env.REACT_APP_BRAINTREE_TOKENIZATION_KEY || 
                   (__DEV__ ? 'sandbox_yzcf2x7q_334zyxsmp8pscyq8' : 'production_...'), // Fallback for dev
  
  // API endpoint for backend (RN: never localhost; use Render HTTPS)
  apiEndpoint: (Platform.OS === 'web' && __DEV__) ? 'http://localhost:3000/api' : 'https://bookapt-payment-api.onrender.com/api',
};

export interface BraintreePaymentRequest {
  amount: number;
  serviceName: string;
  customerId: string;
  businessId: string;
  description?: string;
  customerPhone?: string;
  customerEmail?: string;
}

export interface BraintreePaymentResult {
  success: boolean;
  transactionId?: string;
  paymentMethod?: 'venmo' | 'paypal' | 'credit_card';
  nonce?: string;
  description?: string;
  error?: string;
}

class BraintreeService {
  private isInitialized = false;

  /**
   * Initialize Braintree
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      console.log('Initializing Braintree Payment Service...');
      this.isInitialized = true;
      console.log('Braintree initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Braintree:', error);
      throw error;
    }
  }

  /**
   * Show Braintree Drop-In UI
   * Supports: Venmo, PayPal, Credit Cards
   */
  async showDropIn(request: BraintreePaymentRequest): Promise<BraintreePaymentResult> {
    try {
      console.log('🔧 Showing payment request options for amount:', request.amount);

      // Business owner selects how to send payment request to customer
      return new Promise((resolve) => {
        Alert.alert(
          'Send Payment Request',
          `How would you like to request payment of $${request.amount} from ${request.customerId}?`,
          [
            {
              text: 'Send Venmo Request',
              onPress: () => this.sendVenmoPaymentRequest(request).then(resolve),
            },
            {
              text: 'Send PayPal Request',
              onPress: () => this.sendPayPalPaymentRequest(request).then(resolve),
            },
            {
              text: 'Send SMS Request',
              onPress: () => this.sendSMSPaymentRequest(request).then(resolve),
            },
            {
              text: 'Send Email Request',
              onPress: () => this.sendEmailPaymentRequest(request).then(resolve),
            },
            {
              text: 'Cancel',
              style: 'cancel',
              onPress: () => resolve({
                success: false,
                error: 'Payment request cancelled',
              }),
            },
          ]
        );
      });
    } catch (error: any) {
      console.error('Payment request error:', error);
      return {
        success: false,
        error: error.message || 'Payment request failed',
      };
    }
  }

  /**
   * Process Venmo Payment
   */
  async processVenmoPayment(request: BraintreePaymentRequest): Promise<BraintreePaymentResult> {
    try {
      console.log('Processing Venmo payment:', request);

      // Initialize Braintree if not already done
      await this.initialize();

      // Configure Drop-In specifically for Venmo
      const venmoRequest = {
        amount: request.amount.toString(),
        currencyCode: 'USD',
        merchantAccountId: undefined, // Use default merchant account
        
        // Venmo-specific configuration
        venmo: {
          requestBillingAgreement: false,
          profileId: undefined, // Use default Venmo profile
        },
        
        // Only show Venmo option
        paymentMethodTypes: ['Venmo'],
        
        // UI configuration
        requestThreeDSecureVerification: false,
        collectDeviceData: false,
        
        // Customization
        theme: {
          primaryColor: '#007AFF',
          secondaryColor: '#F0F8FF',
          fontFamily: 'System',
        },
      };

      console.log('🔧 Showing Braintree Drop-In for Venmo:', venmoRequest);

      // Simulate Venmo payment for now
      console.log('🔧 Simulating Venmo payment...');
      const result = { isCancelled: false, paymentMethodNonce: 'venmo_nonce_' + Date.now() };
      
      if (result.isCancelled) {
        return {
          success: false,
          error: 'Payment was cancelled',
        };
      }

      if (result.paymentMethodNonce) {
        console.log('✅ Venmo payment successful:', result);
        
        // Process the payment with the nonce
        const paymentResult = await this.processPaymentNonce(
          result.paymentMethodNonce,
          request.amount,
          request.serviceName
        );

        return {
          success: true,
          transactionId: paymentResult.transactionId,
          paymentMethod: 'venmo',
          amount: request.amount,
          currency: 'USD',
          nonce: result.paymentMethodNonce,
        };
      }

      return {
        success: false,
        error: 'No payment method received',
      };
    } catch (error: any) {
      console.error('Venmo payment error:', error);
      
      // If Venmo is not available, show helpful message
      if (error.message?.includes('Venmo') || error.code === 'VENMO_NOT_AVAILABLE') {
        Alert.alert(
          'Venmo Not Available',
          'Venmo is not available on this device. Please try another payment method.',
          [{ text: 'OK' }]
        );
      }
      
      return {
        success: false,
        error: error.message || 'Venmo payment failed',
      };
    }
  }

  /**
   * Process payment nonce with backend
   */
  private async processPaymentNonce(
    nonce: string, 
    amount: number, 
    serviceName: string
  ): Promise<{ transactionId: string; success: boolean }> {
    try {
      console.log('Processing payment nonce:', { nonce: nonce.substring(0, 10) + '...', amount, serviceName });

      // In production, this would call your backend API
      // For now, we'll simulate the backend processing
      if (__DEV__) {
        console.log('🔧 Development mode: Simulating backend payment processing');
        
        // Simulate backend processing delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return {
          transactionId: `bt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          success: true,
        };
      }

      // Production: Call your backend API to process the payment
      const response = await fetch(`${BRAINTREE_CONFIG.apiEndpoint}/process-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`,
        },
        body: JSON.stringify({
          paymentMethodNonce: nonce,
          amount: amount,
          serviceName: serviceName,
          merchantAccountId: undefined, // Use default
        }),
      });

      if (!response.ok) {
        throw new Error(`Payment processing failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        transactionId: result.transactionId,
        success: result.success,
      };
    } catch (error: any) {
      console.error('Error processing payment nonce:', error);
      throw error;
    }
  }

  /**
   * Get authentication token for backend API calls
   */
  private async getAuthToken(): Promise<string> {
    // In production, this would get the user's auth token
    // For now, return a placeholder
    return 'your_auth_token_here';
  }

  /**
   * Process PayPal Payment
   */
  async processPayPalPayment(request: BraintreePaymentRequest): Promise<BraintreePaymentResult> {
    try {
      console.log('Processing PayPal payment:', request);

      // Initialize Braintree if not already done
      await this.initialize();

      // Configure Drop-In specifically for PayPal
      const paypalRequest = {
        amount: request.amount.toString(),
        currencyCode: 'USD',
        merchantAccountId: undefined,
        
        // PayPal-specific configuration
        paypal: {
          requestBillingAgreement: false,
          currencyCode: 'USD',
          localeCode: 'en_US',
        },
        
        // Only show PayPal option
        paymentMethodTypes: ['PayPal'],
        
        // UI configuration
        requestThreeDSecureVerification: false,
        collectDeviceData: false,
        
        // Customization
        theme: {
          primaryColor: '#0070BA',
          secondaryColor: '#F0F8FF',
          fontFamily: 'System',
        },
      };

      console.log('🔧 Showing Braintree Drop-In for PayPal:', paypalRequest);

      // Use Braintree Drop-In for actual PayPal payment
      try {
        if (Platform.OS === 'web') {
          // Web: Use Braintree Web SDK
          if (!braintreeWeb) {
            throw new Error('Braintree Web SDK is not installed. Run: npm install braintree-web');
          }

          // Initialize Braintree client for web
          const client = await braintreeWeb.client.create({
            authorization: BRAINTREE_CONFIG.tokenizationKey,
          });

          // Create PayPal Checkout instance
          const paypalCheckout = await braintreeWeb.paypalCheckout.create({
            client: client,
          });

          // Create PayPal payment (opens PayPal window for user approval)
          const paypalPaymentOptions = {
            flow: 'checkout',
            amount: request.amount.toString(),
            currency: 'USD',
            intent: 'sale',
          };

          // This opens PayPal popup and returns payment token
          const paypalPayment = await paypalCheckout.createPayment(paypalPaymentOptions);

          // Tokenize the PayPal payment to get nonce
          // This happens after user approves in PayPal popup
          const payload = await paypalCheckout.tokenizePayment(paypalPayment);

          // Return the payment method nonce
          return {
            success: true,
            paymentMethodNonce: payload.nonce,
            paymentMethod: 'paypal',
            amount: request.amount,
            currency: 'USD',
            payerId: payload.details?.payerId,
          };
        } else {
          // Mobile: Show Braintree Drop-In UI
          const result = await BraintreeDropIn.show({
            tokenizationKey: BRAINTREE_CONFIG.tokenizationKey,
            amount: request.amount.toString(),
            currencyCode: 'USD',
          });

          if (result.isCancelled || !result.paymentMethodNonce) {
            return {
              success: false,
              error: 'Payment was cancelled',
            };
          }

          console.log('✅ Braintree payment method obtained:', result.paymentMethodNonce.substring(0, 20) + '...');

          // Return nonce to be processed by backend
          return {
            success: true,
            paymentMethodNonce: result.paymentMethodNonce,
            paymentMethod: 'paypal',
            amount: request.amount,
            currency: 'USD',
          };
        }
      } catch (error: any) {
        console.error('Braintree payment error:', error);
        
        // Handle specific PayPal account linking error
        if (error.code === 'PAYPAL_SANDBOX_ACCOUNT_NOT_LINKED' || error.code === 'PAYPAL_ACCOUNT_NOT_LINKED' || error.message?.includes('linked PayPal')) {
          const isProduction = !BRAINTREE_CONFIG.tokenizationKey.startsWith('sandbox_');
          const accountType = isProduction ? 'PayPal Business' : 'PayPal Sandbox';
          
          return {
            success: false,
            error: `${accountType} account needs to be linked to Braintree. Please link your PayPal account in Braintree Dashboard: Settings → Processing → PayPal → Link PayPal Account`,
            errorCode: error.code,
            helpUrl: isProduction 
              ? 'https://developer.paypal.com/braintree/docs/guides/paypal/testing-go-live#go-live'
              : 'https://developer.paypal.com/braintree/docs/guides/paypal/testing-go-live#linked-paypal-testing'
          };
        }
        
        return {
          success: false,
          error: error.message || 'PayPal payment failed. Please try again or use card payment.',
        };
      }
    } catch (error: any) {
      console.error('PayPal payment error:', error);
      return {
        success: false,
        error: error.message || 'PayPal payment failed',
      };
    }
  }

  /**
   * Process Payment Nonce on Backend
   * In production, this should call your backend API
   */
  private async processPaymentNonce(
    nonce: string,
    request: BraintreePaymentRequest
  ): Promise<BraintreePaymentResult> {
    try {
      // Process payment nonce with backend
      console.log('Processing payment nonce with backend:', nonce.substring(0, 20) + '...');
      
      // Backend will create actual Braintree transaction
      // This endpoint should be in the credits API
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_CREDITS_API_URL || 'https://bookapt-user-credits.onrender.com';
      
      const response = await fetch(`${API_BASE_URL}/api/user/credits/braintree-process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nonce,
          amount: request.amount,
          customerId: request.customerId,
          businessId: request.businessId,
          description: request.description || request.serviceName,
        }),
      });

      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          transactionId: data.transactionId,
          paymentMethod: this.getPaymentMethodFromNonce(nonce),
        };
      } else {
        return {
          success: false,
          error: data.error || 'Payment processing failed',
        };
      }
    } catch (error: any) {
      console.error('Error processing payment nonce:', error);
      return {
        success: false,
        error: error.message || 'Payment processing failed',
      };
    }
  }

  /**
   * Get payment method type from nonce
   */
  private getPaymentMethodFromNonce(nonce: string): 'venmo' | 'paypal' | 'credit_card' {
    // Braintree nonces have prefixes indicating payment type
    if (nonce.includes('venmo')) return 'venmo';
    if (nonce.includes('paypal')) return 'paypal';
    return 'credit_card';
  }

  /**
   * Send Venmo Payment Request to Customer
   */
  async sendVenmoPaymentRequest(request: BraintreePaymentRequest): Promise<BraintreePaymentResult> {
    try {
      console.log('📱 Sending Venmo payment request to customer:', request.customerId);
      
      // Generate payment request link
      const paymentLink = `https://venmo.com/pay-request?amount=${request.amount}&note=${encodeURIComponent(request.serviceName)}&recipient=${request.businessId}`;
      
      // Initialize Twilio with real credentials
      await twilioService.initialize({
        accountSid: 'AC104761483cc1d5e690eab8fe6d673761',
        authToken: 'ee8bcc26752e44305808c7b1ef9185b2',
        phoneNumber: '+18778843550',
      });

      // Send SMS with Venmo link (if phone number available)
      let smsResult = { success: true };
      if (request.customerPhone) {
        smsResult = await twilioService.sendVenmoLinkSMS(
          request.customerPhone,
          request.amount,
          request.serviceName,
          'BookAPT Business', // TODO: Get from business profile
          request.businessId // Venmo username
        );
      }

      // Send email with Venmo link (if email available)
      let emailResult = { success: true };
      if (request.customerEmail) {
        emailResult = await emailService.sendPaymentRequestEmail(
          request.customerEmail,
          request.amount,
          request.serviceName,
          'Your Business', // TODO: Get from business profile
          {
            venmo: `@${request.businessId}`,
            paypal: undefined,
            creditCard: true,
          }
        );
      }

      // Add in-app notification
      notificationService.addPaymentRequestNotification(request.customerId, {
        appointmentId: request.businessId, // TODO: Get actual appointment ID
        amount: request.amount,
        serviceName: request.serviceName,
        businessName: 'Your Business',
        paymentMethod: 'venmo',
        paymentLink,
      });

      // Show success message
      const contactMethods = [];
      if (request.customerPhone) contactMethods.push('SMS');
      if (request.customerEmail) contactMethods.push('email');
      contactMethods.push('in-app notification');
      
      Alert.alert(
        'Venmo Payment Request Sent!',
        `Payment request of $${request.amount} has been sent to ${request.customerId} via ${contactMethods.join(', ')}.\n\nCustomer will receive a link to pay via Venmo.`,
        [{ text: 'OK' }]
      );

      return {
        success: true,
        transactionId: `venmo_request_${Date.now()}`,
        paymentMethod: 'venmo',
        description: `Venmo payment request sent to ${request.customerId}`,
      };
    } catch (error: any) {
      console.error('Venmo payment request error:', error);
      return {
        success: false,
        error: error.message || 'Failed to send Venmo payment request',
      };
    }
  }

  /**
   * Send PayPal Payment Request to Customer
   */
  async sendPayPalPaymentRequest(request: BraintreePaymentRequest): Promise<BraintreePaymentResult> {
    try {
      console.log('💳 Sending PayPal payment request to customer:', request.customerId);
      
      // Generate PayPal payment request link
      const paymentLink = `https://paypal.me/${request.businessId}/${request.amount}?note=${encodeURIComponent(request.serviceName)}`;
      
      // Initialize Twilio with real credentials
      await twilioService.initialize({
        accountSid: 'AC104761483cc1d5e690eab8fe6d673761',
        authToken: 'ee8bcc26752e44305808c7b1ef9185b2',
        phoneNumber: '+18778843550',
      });

      // Send SMS with PayPal link (if phone number available)
      let smsResult = { success: true };
      if (request.customerPhone) {
        smsResult = await twilioService.sendPayPalLinkSMS(
          request.customerPhone,
          request.amount,
          request.serviceName,
          'BookAPT Business', // TODO: Get from business profile
          request.businessId // PayPal username
        );
      }

      // Send email with PayPal link (if email available)
      let emailResult = { success: true };
      if (request.customerEmail) {
        emailResult = await emailService.sendPaymentRequestEmail(
          request.customerEmail,
          request.amount,
          request.serviceName,
          'Your Business', // TODO: Get from business profile
          {
            venmo: undefined,
            paypal: `@${request.businessId}`,
            creditCard: true,
          }
        );
      }

      // Add in-app notification
      notificationService.addPaymentRequestNotification(request.customerId, {
        appointmentId: request.businessId, // TODO: Get actual appointment ID
        amount: request.amount,
        serviceName: request.serviceName,
        businessName: 'Your Business',
        paymentMethod: 'paypal',
        paymentLink,
      });

      // Show success message
      const contactMethods = [];
      if (request.customerPhone) contactMethods.push('SMS');
      if (request.customerEmail) contactMethods.push('email');
      contactMethods.push('in-app notification');
      
      Alert.alert(
        'PayPal Payment Request Sent!',
        `Payment request of $${request.amount} has been sent to ${request.customerId} via ${contactMethods.join(', ')}.\n\nCustomer will receive a link to pay via PayPal.`,
        [{ text: 'OK' }]
      );

      return {
        success: true,
        transactionId: `paypal_request_${Date.now()}`,
        paymentMethod: 'paypal',
        description: `PayPal payment request sent to ${request.customerId}`,
      };
    } catch (error: any) {
      console.error('PayPal payment request error:', error);
      return {
        success: false,
        error: error.message || 'Failed to send PayPal payment request',
      };
    }
  }

  /**
   * Send SMS Payment Request to Customer
   */
  async sendSMSPaymentRequest(request: BraintreePaymentRequest): Promise<BraintreePaymentResult> {
    try {
      console.log('📱 Sending SMS payment request to customer:', request.customerId);
      
      const message = `Hi! Please pay $${request.amount} for ${request.serviceName}. Payment options: Venmo, PayPal, or Credit Card. Reply with your preferred method.`;
      
      // In a real app, this would send actual SMS
      Alert.alert(
        'SMS Payment Request Sent!',
        `SMS sent to ${request.customerId}:\n\n"${message}"`,
        [{ text: 'OK' }]
      );

      return {
        success: true,
        transactionId: `sms_request_${Date.now()}`,
        paymentMethod: 'sms',
        description: `SMS payment request sent to ${request.customerId}`,
      };
    } catch (error: any) {
      console.error('SMS payment request error:', error);
      return {
        success: false,
        error: error.message || 'Failed to send SMS payment request',
      };
    }
  }

  /**
   * Send Email Payment Request to Customer
   */
  async sendEmailPaymentRequest(request: BraintreePaymentRequest): Promise<BraintreePaymentResult> {
    try {
      console.log('📧 Sending email payment request to customer:', request.customerId);
      
      const subject = `Payment Request - ${request.serviceName}`;
      const body = `Dear Customer,\n\nPlease pay $${request.amount} for ${request.serviceName}.\n\nPayment options:\n- Venmo: @${request.businessId}\n- PayPal: paypal.me/${request.businessId}\n- Credit Card: Contact us\n\nThank you!`;
      
      // In a real app, this would send actual email
      Alert.alert(
        'Email Payment Request Sent!',
        `Email sent to ${request.customerId} with payment details.`,
        [{ text: 'OK' }]
      );

      return {
        success: true,
        transactionId: `email_request_${Date.now()}`,
        paymentMethod: 'email',
        description: `Email payment request sent to ${request.customerId}`,
      };
    } catch (error: any) {
      console.error('Email payment request error:', error);
      return {
        success: false,
        error: error.message || 'Failed to send email payment request',
      };
    }
  }

  /**
   * Get client token from backend
   * In production, client tokens should come from your backend
   * and include customer-specific information
   */
  async getClientToken(customerId: string): Promise<string | null> {
    try {
      if (__DEV__) {
        // In test mode, use the tokenization key
        return BRAINTREE_CONFIG.tokenizationKey;
      }

      const response = await fetch(`${BRAINTREE_CONFIG.apiEndpoint}/payment/client-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId }),
      });

      const data = await response.json();
      return data.clientToken;
    } catch (error) {
      console.error('Error getting client token:', error);
      return null;
    }
  }
}

// Export singleton instance
export const braintreeService = new BraintreeService();
export default braintreeService;

